import { FuelRobFormState } from '@navstation-portal/reporting/data-model/offshore-report';
import { StockTankFormState, TankTransferFormState } from '@navstation-portal/reporting/data-model/vessel-report';
import { NavStatus } from '@navstation-portal/shared/data-model/ui';
import { VesselSpec } from '@navstation-portal/vessels/data-model/vessel';
import { isStateField } from '../../offshore-vm-builder-helpers';
import { sortAndEnrichTanksByVesselSpec } from './with-offshore-stock-effects';

export type OffshoreStockVm = ReturnType<typeof buildOffshoreStockVm>;

export interface OffshoreStockFormVmSections {
  tanks: () => { tankRows: StockTankFormState[] | undefined };
  tankTransfers: () => { transfers: TankTransferFormState[] };
  fuelRob: () => {
    fuelRobRows: FuelRobFormState[];
  };
}

export function buildOffshoreStockVm(state: OffshoreStockFormVmSections, vesselSpec: VesselSpec | undefined) {
  if (!vesselSpec || !state) {
    return {
      inUseTanks: undefined,
      notInUseTanks: undefined,
      allTanks: undefined,
      hasDuplicateTanksSelected: false,
      isAllTanksStatus: () => 'invalid' as NavStatus,
      notInUseTanksStatus: () => 'invalid' as NavStatus,
      inUseTanksStatus: () => 'invalid' as NavStatus,
      hasStartedTankTransfers: () => false,
      tankTransfersStatus: () => 'pending' as NavStatus,
      fuelRobTableStatus: () => 'valid' as NavStatus,
      inUseTankTotals: {},
      tankOptionsWithDisabled: [],
      isTransferRowRequired: () => false,
    };
  }

  const tankTransfers = state.tankTransfers()?.transfers ?? [];

  const rawTankRows = state.tanks()?.tankRows as StockTankFormState[] | undefined;

  const tankRows = rawTankRows ? sortAndEnrichTanksByVesselSpec(rawTankRows, vesselSpec.tanks) : undefined;

  const tanksValid = (tankRows: StockTankFormState[] | FuelRobFormState[] | undefined) => {
    if (!tankRows || isStateField(tankRows)) return false;
    return tankRows.every((row) =>
      Object.values(row).every((field) => field.status === 'VALID' || field.status === 'DISABLED')
    );
  };

  const inUseTanks = tankRows ? filterStockTanksByInUse(tankRows, true) : undefined;

  const notInUseTanks = tankRows ? filterStockTanksByInUse(tankRows, false) : undefined;

  const hasStartedTankTransfers = () => tankTransfers.some(isTransferStarted);

  const tankTransfersStatus = (): NavStatus => {
    const started = tankTransfers.filter(isTransferStarted);
    if (started.length === 0) return 'pending';
    const allComplete = started.every(
      ({ from, to, quantity }) =>
        typeof from.value?.code === 'string' && typeof to.value?.code === 'string' && quantity.value != null
    );
    return allComplete ? 'valid' : 'invalid';
  };

  const inUseTankTotals = computeInUseTankTotals(inUseTanks);

  const hasDuplicateTanksSelected = computeHasDuplicateTanksSelected(tankTransfers);
  const tankOptionsWithDisabled = buildTankOptionsWithDisabled(tankTransfers, vesselSpec);

  const isTransferRowRequired = (index: number): boolean => {
    const transfer = tankTransfers[index];
    if (!transfer) return false;
    const transferHasValue = isTransferStarted(transfer);
    return !transferHasValue ? false : true;
  };

  const stockFuelTotals = calculateStockFuelTotals(rawTankRows);
  const isAllTanksValid = () => tanksValid(tankRows);
  const fuelRobTableValid = () => tanksValid(state.fuelRob().fuelRobRows);
  const isAllTanksStatus = (): NavStatus => (isAllTanksValid() ? 'valid' : 'invalid');
  const fuelRobTableStatus = () => (fuelRobTableValid() ? 'valid' : 'invalid');
  const notInUseTanksStatus = () => (tanksValid(notInUseTanks) ? 'valid' : 'invalid');
  const inUseTanksStatus = () => (tanksValid(inUseTanks) ? 'valid' : 'invalid');
  return {
    inUseTanks,
    notInUseTanks,
    allTanks: tankRows,
    hasDuplicateTanksSelected,
    isAllTanksStatus,
    stockFuelTotals,
    notInUseTanksStatus,
    inUseTanksStatus,
    hasStartedTankTransfers,
    tankTransfersStatus,
    fuelRobTableStatus,
    inUseTankTotals,
    tankOptionsWithDisabled,
    isTransferRowRequired,
  };
}

function calculateStockFuelTotals(tankRows?: StockTankFormState[]): Map<string, number> | undefined {
  if (!tankRows) return;

  const totalsMap = new Map<string, number>();
  Object.values(tankRows).forEach((tank) => {
    const fuelGrade: string = (tank.fuelGrade?.value?.code as string) || 'UNKNOWN';
    const amount = tank.amount?.value || 0;

    if (totalsMap.has(fuelGrade)) {
      totalsMap.set(fuelGrade, (totalsMap.get(fuelGrade) ?? 0) + amount);
    } else {
      totalsMap.set(fuelGrade, amount);
    }
  });

  return totalsMap;
}

function isTransferStarted({ from, to, quantity }: TankTransferFormState): boolean {
  return typeof from.value?.code === 'string' || typeof to.value?.code === 'string' || quantity.value != null;
}

function computeInUseTankTotals(
  inUseTanks: StockTankFormState[] | undefined
): Record<string, { currentMT: number; amountBeforeBunkering: number }> {
  if (!inUseTanks) return {};
  return Object.values(inUseTanks).reduce<Record<string, { currentMT: number; amountBeforeBunkering: number }>>(
    (acc, tank) => {
      const fuelType = (tank.fuelGrade?.value as { name: string })?.name ?? 'Unknown';
      const currentMT = (tank.amount?.value as number) ?? 0;
      const before = (tank.amountBeforeBunkering?.value as number) ?? 0;
      const existing = acc[fuelType];
      acc[fuelType] = {
        currentMT: (existing?.currentMT ?? 0) + currentMT,
        amountBeforeBunkering: (existing?.amountBeforeBunkering ?? 0) + before,
      };
      return acc;
    },
    {}
  );
}

function computeHasDuplicateTanksSelected(transfers: TankTransferFormState[]): boolean {
  const tankIds = transfers.flatMap(({ from, to }) => {
    const ids: string[] = [];
    if (from.value?.code) ids.push(from.value.code as string);
    if (to.value?.code) ids.push(to.value.code as string);
    return ids;
  });
  return new Set(tankIds).size < tankIds.length;
}

export function buildTankOptionsWithDisabled(transfers: TankTransferFormState[], vesselSpec: VesselSpec) {
  const tankOptions = vesselSpec.tanks.map((tank) => ({
    name: tank.name,
    code: tank.tank_id.$oid as string,
  }));

  const selectedCodes = new Set(
    transfers.flatMap(({ from, to }) => {
      const codes: string[] = [];
      if (from.value?.code) codes.push(from.value.code as string);
      if (to.value?.code) codes.push(to.value.code as string);
      return codes;
    })
  );

  const withDisabled = (current?: string) =>
    tankOptions.map((opt) => ({
      ...opt,
      disabled: opt.code !== current && selectedCodes.has(opt.code),
    }));

  return transfers.map((t) => ({
    from: withDisabled(t.from.value?.code as string),
    to: withDisabled(t.to.value?.code as string),
  }));
}

function filterStockTanksByInUse(tanks: StockTankFormState[], inUse: boolean): StockTankFormState[] | undefined {
  const filtered = tanks.filter((tank) => tank.inUse?.value === inUse);
  return filtered.length > 0 ? filtered : undefined;
}
